import React, { useState } from 'react';

interface CodeHighlight {
  line: number;
  reason: string;
}

interface CodeSnippetVisualizationProps {
  title: string;
  language: string;
  code: string;
  explanation?: string;
  highlights?: CodeHighlight[];
  performance_note?: string;
  github_url?: string;
  isDarkMode: boolean;
}

const CodeSnippetVisualization: React.FC<CodeSnippetVisualizationProps> = ({
  title,
  language,
  code,
  explanation,
  highlights = [],
  performance_note,
  github_url,
  isDarkMode
}) => {
  const [copied, setCopied] = useState(false);
  const [hoveredLine, setHoveredLine] = useState<number | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy code:', error);
    }
  };

  const codeLines = code.split('\n');
  const highlightedLines = new Set(highlights.map(h => h.line - 1)); // Convert to 0-based indexing

  const getLanguageIcon = (lang: string) => {
    const icons: Record<string, string> = {
      'typescript': '🔷',
      'javascript': '🟨',
      'python': '🐍',
      'java': '☕',
      'cpp': '⚡',
      'rust': '🦀',
      'go': '🐹',
      'html': '🌐',
      'css': '🎨',
      'sql': '🗃️',
      'bash': '🐚',
      'json': '📋'
    };
    return icons[lang.toLowerCase()] || '📝';
  };

  const getLanguageColor = (lang: string) => {
    const colors: Record<string, string> = {
      'typescript': '#3178C6',
      'javascript': '#F7DF1E',
      'python': '#3776AB',
      'java': '#ED8B00',
      'cpp': '#00599C',
      'rust': '#000000',
      'go': '#00ADD8',
      'html': '#E34F26',
      'css': '#1572B6',
      'sql': '#336791',
      'bash': '#4EAA25',
      'json': '#000000'
    };
    return colors[lang.toLowerCase()] || '#6B7280';
  };

  return (
    <div style={{
      margin: '16px 0',
      borderRadius: '16px',
      overflow: 'hidden',
      background: isDarkMode 
        ? 'linear-gradient(145deg, rgba(15,23,42,0.9), rgba(30,41,59,0.8))'
        : 'linear-gradient(145deg, #ffffff, #f8fafc)',
      border: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.08)'}`,
      boxShadow: isDarkMode 
        ? '0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)'
        : '0 20px 40px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.03)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 20px',
        background: isDarkMode 
          ? 'linear-gradient(90deg, rgba(30,41,59,0.8), rgba(51,65,85,0.6))'
          : 'linear-gradient(90deg, #f1f5f9, #e2e8f0)',
        borderBottom: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            background: `${getLanguageColor(language)}15`,
            border: `1px solid ${getLanguageColor(language)}30`
          }}>
            <span style={{ fontSize: '16px' }}>
              {getLanguageIcon(language)}
            </span>
          </div>
          <div>
            <div style={{
              fontWeight: '700',
              fontSize: '16px',
              color: isDarkMode ? '#f1f5f9' : '#0f172a',
              marginBottom: '2px'
            }}>
              {title}
            </div>
            <div style={{
              fontSize: '12px',
              color: getLanguageColor(language),
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {language}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {github_url && (
            <a
              href={github_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                background: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                color: isDarkMode ? '#94a3b8' : '#64748b',
                textDecoration: 'none',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
              }}
            >
              <span>🔗</span> View on GitHub
            </a>
          )}
          
          <button
            onClick={handleCopy}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none',
              background: copied 
                ? (isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.1)')
                : (isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
              color: copied 
                ? (isDarkMode ? '#4ade80' : '#16a34a')
                : (isDarkMode ? '#94a3b8' : '#64748b'),
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              if (!copied) {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!copied) {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
              }
            }}
          >
            <span>{copied ? '✅' : '📋'}</span>
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Code Block */}
      <div style={{
        position: 'relative',
        background: isDarkMode 
          ? 'linear-gradient(145deg, rgba(15,23,42,0.95), rgba(30,41,59,0.9))'
          : 'linear-gradient(145deg, #ffffff, #fefefe)',
      }}>
        <pre style={{
          margin: 0,
          padding: '20px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
          fontSize: '14px',
          lineHeight: '1.6',
          color: isDarkMode ? '#e2e8f0' : '#334155',
          overflow: 'auto',
          maxHeight: '500px'
        }}>
          {codeLines.map((line, index) => {
            const isHighlighted = highlightedLines.has(index);
            const highlight = highlights.find(h => h.line - 1 === index);
            
            return (
              <div
                key={index}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '24px',
                  background: isHighlighted 
                    ? (isDarkMode ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)')
                    : 'transparent',
                  marginLeft: '-20px',
                  marginRight: '-20px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  borderLeft: isHighlighted 
                    ? '3px solid #3b82f6' 
                    : '3px solid transparent',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={() => setHoveredLine(index)}
                onMouseLeave={() => setHoveredLine(null)}
              >
                <span style={{
                  display: 'inline-block',
                  width: '30px',
                  textAlign: 'right',
                  marginRight: '16px',
                  color: isDarkMode ? 'rgba(148,163,184,0.4)' : 'rgba(100,116,139,0.4)',
                  fontSize: '12px',
                  userSelect: 'none'
                }}>
                  {index + 1}
                </span>
                <code style={{ flex: 1 }}>{line}</code>
                
                {/* Highlight tooltip */}
                {isHighlighted && hoveredLine === index && highlight && (
                  <div style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: isDarkMode ? 'rgba(15,23,42,0.95)' : '#ffffff',
                    border: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.1)'}`,
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    color: isDarkMode ? '#cbd5e1' : '#475569',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    whiteSpace: 'normal',
                    zIndex: 10,
                    maxWidth: '250px'
                  }}>
                    💡 {highlight.reason}
                  </div>
                )}
              </div>
            );
          })}
        </pre>
      </div>

      {/* Footer with explanation and performance note */}
      {(explanation || performance_note) && (
        <div style={{
          padding: '16px 20px',
          background: isDarkMode 
            ? 'linear-gradient(90deg, rgba(30,41,59,0.6), rgba(51,65,85,0.4))'
            : 'linear-gradient(90deg, #f8fafc, #f1f5f9)',
          borderTop: `1px solid ${isDarkMode ? 'rgba(148,163,184,0.1)' : 'rgba(15,23,42,0.06)'}`
        }}>
          {explanation && (
            <div style={{
              fontSize: '14px',
              color: isDarkMode ? '#cbd5e1' : '#475569',
              marginBottom: performance_note ? '12px' : '0',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px', marginTop: '1px' }}>💡</span>
              <span>{explanation}</span>
            </div>
          )}
          
          {performance_note && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              background: isDarkMode ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)'}`
            }}>
              <span style={{ fontSize: '16px' }}>⚡</span>
              <span style={{
                fontSize: '13px',
                fontWeight: '500',
                color: isDarkMode ? '#4ade80' : '#16a34a'
              }}>
                Performance: {performance_note}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CodeSnippetVisualization;